import { Relations } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import { ActionItemByIdMap, adminActionItemsQueryKey } from "../action-item.keys";
import { ActionItem } from "../types";

export const useDeleteActionItem = () => {
	const queryClient = useQueryClient();
	const { _delete } = useRequest();

	const mutation = useMutation({
		mutationFn: async ({
			actionItemId,
		}: {
			actionItemId: ActionItem["id"];
			relation: Relations.ActionItems;
		}) => await _delete(`/v2/action-items/${actionItemId}`, undefined, "none"),
		onMutate: async ({
			actionItemId,
			relation,
		}: {
			actionItemId: ActionItem["id"];
			relation: Relations.ActionItems;
		}) => {
			await queryClient.cancelQueries({
				queryKey: adminActionItemsQueryKey.actionItemsByRelation(relation),
			});

			const previousActionItems = queryClient.getQueryData<ActionItemByIdMap>(
				adminActionItemsQueryKey.actionItemsByRelation(relation),
			);

			queryClient.setQueryData<ActionItemByIdMap>(
				adminActionItemsQueryKey.actionItemsByRelation(relation),
				(old) => {
					if (old === undefined) return old;
					const { [actionItemId]: _deleted, ...rest } = old;
					return rest;
				},
			);

			return { previousActionItems, relation };
		},
		onError: (_error, _variables, context) => {
			if (context === undefined) return;

			queryClient.setQueryData(
				adminActionItemsQueryKey.actionItemsByRelation(context.relation),
				context.previousActionItems,
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: adminActionItemsQueryKey.all(), exact: true });
		},
	});

	return {
		deleteActionItem: mutation.mutate,
		deleteActionItemLoading: mutation.isPending,
	};
};
